// 099 运营后台 · 共享小组件与格式化（零新依赖）
import type { ReactNode } from "react";

/** KPI 卡；value 为 null 时显示"未接入/未采集"诚实态（099 纪律：不编造数据） */
export function Kpi(props: {
  label: string; value: number | string | null; delta?: string | null;
  deltaKind?: "up" | "down" | "flat"; kind?: "warn" | "bad"; hint?: string; naText?: string;
}) {
  const na = props.value === null;
  return (
    <div className={`d-card d-kpi${props.kind ? ` ${props.kind}` : ""}`}>
      <div className="k">{props.label}{props.hint ? <span className="d-tagb">{props.hint}</span> : null}</div>
      {na
        ? <div className="v na">{props.naText ?? "未接入"}</div>
        : <div className="v">{props.value}</div>}
      <div className={`d ${props.deltaKind ?? "flat"}`}>{na ? "数据源缺失时不编造" : (props.delta ?? " ")}</div>
    </div>
  );
}

export function Sect(props: { title: string; tag?: string; hint?: string }) {
  return (
    <div className="d-sect">
      {props.title}
      {props.tag ? <span className="d-tagb">{props.tag}</span> : null}
      {props.hint ? <span className="hint">{props.hint}</span> : null}
    </div>
  );
}

export function Note(props: { children: ReactNode }) {
  return <div className="d-note">{props.children}</div>;
}

export function Bar(props: { pct: number; kind?: "g" | "y" | "r" | "c" }) {
  return (
    <div className="bar-bg">
      <div className={`bar${props.kind ? ` ${props.kind}` : ""}`} style={{ width: `${Math.min(100, Math.max(0, props.pct))}%` }} />
    </div>
  );
}

/** 天数切换段控 */
export function DaySeg(props: { days: number; onChange: (d: number) => void }) {
  const opts = [
    { d: 1, label: "今天" }, { d: 2, label: "昨天起" }, { d: 7, label: "近 7 天" }, { d: 30, label: "近 30 天" },
  ];
  return (
    <div className="d-seg">
      {opts.map((o) => (
        <button key={o.d} className={props.days === o.d ? "on" : ""} onClick={() => props.onChange(o.d)}>{o.label}</button>
      ))}
    </div>
  );
}

// ---------- 格式化 ----------

export const fmtMoney = (cents: number) => `¥${(cents / 100).toFixed(2)}`;

export const fmtTokens = (n: number) =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(2)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}K` : String(n);

export function fmtMs(ms: number | null): string {
  if (ms === null || ms === undefined) return "—";
  const min = ms / 60_000;
  if (min < 1) return `${Math.round(ms / 1000)} 秒`;
  if (min < 60) return `${min.toFixed(1)} 分钟`;
  return `${(min / 60).toFixed(1)} 小时`;
}

export function fmtTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return new Intl.DateTimeFormat("zh-CN", {
    timeZone: "Asia/Shanghai", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).format(d).replace("/", "-");
}

export function fmtPct(v: number | null): string {
  return v === null ? "—" : `${(v * 100).toFixed(1)}%`;
}

/** 环比文案；null = 上期为 0（不显示误导百分比） */
export function pctDelta(v: number | null): { text: string; kind: "up" | "down" | "flat" } {
  if (v === null) return { text: "上期为 0，不计环比", kind: "flat" };
  const pct = `${v >= 0 ? "+" : ""}${(v * 100).toFixed(1)}%`;
  return { text: `较上期 ${pct}`, kind: v > 0 ? "up" : v < 0 ? "down" : "flat" };
}

/** 任务状态徽章 */
export function StatusTag(props: { status: string | null; quality?: string | null; degraded?: boolean }) {
  const s = props.status;
  if (!s) return <span className="tag t-gray">未生成</span>;
  if (s === "done") {
    if (props.quality === "certified") return <span className="tag t-green">✓ 完成 · 认证</span>;
    if (props.quality === "needs_revision") return <><span className="tag t-green">✓ 完成</span> <span className="tag t-yellow">需修订</span></>;
    if (props.quality === "human_review") return <><span className="tag t-green">✓ 完成</span> <span className="tag t-yellow">待人工评审</span></>;
    return <span className="tag t-green">✓ 完成{props.degraded ? " · 降级" : ""}</span>;
  }
  if (s === "failed") return <span className="tag t-red">✗ 失败</span>;
  if (s === "canceled") return <span className="tag t-gray">已取消</span>;
  return <span className="tag t-yellow">● {s}（进行中）</span>;
}
